import type { ListBuilderConfiguration } from '../types';

/**
 * Item Lot Warehouse Embedded list configuration
 * Shows lots in a specific warehouse
 * Converted from JSON to TypeScript for type safety
 */
export const ITEM_LOT_WAREHOUSE_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-item-lots-warehouse-embedded',
			cacheTTL: 600000,
			name: 'Warehouse Lots',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'lot-number',
					fields: [
						{
							id: 'lot-number-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lotNumber' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'lb1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'external-id-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'externalId' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Lot Number' }],
					minWidth: 150,
					sortable: true,
					alignment: 'left',
					sortFields: [{ direction: 'ASC', fieldName: 'lotNumber' }],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'item-info',
					fields: [
						{
							id: 'item-name-field',
							link: {
								type: 'custom',
								enabled: true,
								customRoute: '/items/{{itemId}}',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'itemName' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'lb1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'item-sku-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'itemSku' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h2', text: 'Item' }],
					minWidth: 250,
					sortable: true,
					alignment: 'left',
					sortFields: [{ direction: 'ASC', fieldName: 'itemName' }],
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'middle',
				},
				{
					id: 'quantity-remaining',
					fields: [
						{
							id: 'quantity-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityRemaining' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h3', text: 'Qty Remaining' }],
					minWidth: 120,
					sortable: true,
					alignment: 'right',
					sortFields: [{ direction: 'DESC', fieldName: 'quantityRemaining' }],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'expiration-date',
					fields: [
						{
							id: 'expiration-date-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'expirationDate' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h4', text: 'Expiration' }],
					minWidth: 120,
					sortable: true,
					alignment: 'center',
					sortFields: [{ direction: 'ASC', fieldName: 'expirationDate' }],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'production-date',
					fields: [
						{
							id: 'production-date-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'productionDate' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h5', text: 'Production' }],
					minWidth: 120,
					sortable: true,
					alignment: 'center',
					sortFields: [{ direction: 'DESC', fieldName: 'productionDate' }],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
			],
			description: 'Lots in this warehouse',
			defaultSortId: 'column-expiration-date-asc',
			requiredFields: [
				'itemLot.id',
				'itemLot.lotNumber',
				'itemLot.itemId',
				'itemLot.warehouseId',
				'itemLot.quantityRemaining',
				'itemLot.expirationDate',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [
			'itemLotKeywords',
			'itemLotExpirationDate',
			'itemLotProductionDate',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/lots/:id',
			objectLabel: 'Item Lot',
			objectTable: 'item_lot',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'lotNumber', fieldLabel: 'Lot Number' },
				{ fieldName: 'quantityRemaining', fieldLabel: 'Quantity Remaining' },
				{ fieldName: 'expirationDate', fieldLabel: 'Expiration Date' },
				{ fieldName: 'productionDate', fieldLabel: 'Production Date' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
			],
		},
	],
	tableSorts: [
		{
			id: 'lot-number',
			name: 'Lot Number',
			fields: [{ direction: 'ASC', fieldName: 'lotNumber' }],
			description: 'Sort by lot number',
		},
		{
			id: 'expiration-date',
			name: 'Expiration Date',
			fields: [{ direction: 'ASC', fieldName: 'expirationDate' }],
			description: 'Sort by expiration date (earliest first)',
		},
		{
			id: 'quantity',
			name: 'Quantity',
			fields: [{ direction: 'DESC', fieldName: 'quantityRemaining' }],
			description: 'Sort by quantity remaining (highest first)',
		},
		{
			id: 'production-date',
			name: 'Production Date',
			fields: [{ direction: 'DESC', fieldName: 'productionDate' }],
			description: 'Sort by production date (newest first)',
		},
	],
};
